/* ======================================================
   REVIEW FORM
====================================================== */

const REVIEW_FORM_ERROR_CLASS = '!border-[#E9282B]';

class ReviewForm extends HTMLElement {
    constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
    }

    connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.form = this.querySelector('form');
        this.steps = Array.from(this.querySelectorAll('[data-step]'));
        this.progressItems = Array.from(
            this.querySelectorAll('[data-step-index]')
        );

        if (!this.form) return;

        this.currentStep = 1;

        this.initCharCounters();
        this.initFileUpload();
        this.initStarRating();

        this.update();

        this.addEventListener('click', this.onClick);

        // A red error border disappears as soon as the field is fixed, not only on the next "Next step".
        // Clicks too, because the custom dropdowns set their value without an input event.
        const clearFixedErrors = () => {
            if (this.querySelector(`.${CSS.escape(REVIEW_FORM_ERROR_CLASS)}`)) this.validateStep(this.currentStep, true);
        };
        this.addEventListener('input', clearFixedErrors);
        this.addEventListener('change', clearFixedErrors);
        this.addEventListener('click', () => setTimeout(clearFixedErrors));
        this.form.addEventListener('submit', this.onSubmit);
    }

    /* =========================
       STEP NAVIGATION
    ========================== */

    onClick(e) {
        const btn = e.target.closest('[data-step-action]');
        if (!btn) return;

        if (btn.dataset.stepAction === 'close-drawer') {
            this.closest('side-drawer')?.close();
            return;
        }

        const next =
            btn.dataset.stepAction === 'step-1'
                ? 1
                : btn.dataset.stepAction === 'step-2'
                    ? 2
                    : btn.dataset.stepAction === 'step-3'
                        ? 3
                        : null;

        if (!next) return;

        if (next > this.currentStep && !this.validateStep(this.currentStep)) {
            return;
        }

        this.goTo(next);
    }

    goTo(step) {
        this.currentStep = step;
        this.update();
    }

    update() {
        this.steps.forEach((el) => {
            el.hidden = this.currentStep === 'success'
                ? el.dataset.step !== 'success'
                : el.dataset.step !== String(this.currentStep);
        });

        const stepNum =
            this.currentStep === 'success' ? 4 : Number(this.currentStep);

        this.progressItems.forEach((el) => {
            const index = Number(el.dataset.stepIndex);
            const active = index <= stepNum;
            const completed = stepNum > index;

            el.classList.toggle('bg-gray-600', active);
            el.classList.toggle('border-gray-600', active);
            el.classList.toggle('text-gray-100', active);

            el.classList.toggle('bg-transparent', !active);
            el.classList.toggle('border-gray-400', !active);
            el.classList.toggle('text-gray-500', !active);

            const check = el.nextElementSibling;
            if (check?.hasAttribute?.('data-review-check')) {
                check.classList.toggle('!block', completed);
                check.classList.toggle('hidden', !completed);
            }
        });
    }

    /* =========================
       VALIDATION
    ========================== */

    // With clearOnly, nothing new is marked red: errors that have been fixed in the meantime are just removed
    validateStep(step, clearOnly = false) {
        const stepEl = this.steps.find(
            (el) => el.dataset.step === String(step)
        );
        if (!stepEl) return true;

        let valid = true;

        /* ---- Native required fields ---- */
        const requiredFields = stepEl.querySelectorAll(
            '[required]:not([type="radio"])'
        );
        let firstInvalidField = null;

        requiredFields.forEach((field) => {
            if (!field.checkValidity()) {
                valid = false;
                if (clearOnly) return;
                field.classList.add(REVIEW_FORM_ERROR_CLASS);
                if (!firstInvalidField) firstInvalidField = field;
            } else {
                field.classList.remove(REVIEW_FORM_ERROR_CLASS);
            }
        });
        if (firstInvalidField) {
            firstInvalidField.reportValidity();
            firstInvalidField.focus();
        }

        /* ---- Required radio groups ---- */
        const radioGroups = new Set(
            Array.from(
                stepEl.querySelectorAll('input[type="radio"][required]')
            ).map((r) => r.name)
        );

        radioGroups.forEach((name) => {
            const radios = stepEl.querySelectorAll(
                `input[type="radio"][name="${name}"]`
            );
            const checked = Array.from(radios).some((r) => r.checked);
            const fieldset = radios[0]?.closest('fieldset');
            const starContainer = fieldset?.querySelector('[data-rating-stars]');

            if (!checked) {
                valid = false;
                if (clearOnly) return;
                if (starContainer) {
                    starContainer.classList.add('border', REVIEW_FORM_ERROR_CLASS);
                } else {
                    radios.forEach((r) => {
                        const fake = r.closest('label')?.querySelector('div[class*="border"]');
                        fake?.classList.add(REVIEW_FORM_ERROR_CLASS);
                    });
                }
                radios[0]?.focus();
            } else {
                if (starContainer) {
                    starContainer.classList.remove('border', REVIEW_FORM_ERROR_CLASS);
                } else {
                    radios.forEach((r) => {
                        const fake = r.closest('label')?.querySelector('div[class*="border"]');
                        fake?.classList.remove(REVIEW_FORM_ERROR_CLASS);
                    });
                }
            }
        });

        /* ---- Checkbox groups ---- */
        stepEl.querySelectorAll('fieldset').forEach((fieldset) => {
            const checkboxes =
                fieldset.querySelectorAll('input[type="checkbox"]');
            if (!checkboxes.length) return;

            const checked = Array.from(checkboxes).some(
                (cb) => cb.checked
            );

            if (!checked) {
                valid = false;
                if (clearOnly) return;
                checkboxes.forEach((cb) => {
                    const fake = cb.closest('label')?.querySelector('div[class*="border"]');
                    fake?.classList.add(REVIEW_FORM_ERROR_CLASS);
                });
            } else {
                checkboxes.forEach((cb) => {
                    const fake = cb.closest('label')?.querySelector('div[class*="border"]');
                    fake?.classList.remove(REVIEW_FORM_ERROR_CLASS);
                });
            }
        });

        /* ---- Custom dropdowns (only when hidden input has required) ---- */
        let firstInvalidCombobox = null;
        stepEl.querySelectorAll('custom-dropdown').forEach(
            (dropdown) => {
                const hiddenInput =
                    dropdown.querySelector('input[type="hidden"]');
                if (
                    !hiddenInput ||
                    !hiddenInput.hasAttribute('required')
                )
                    return;

                const label = dropdown.querySelector('label');
                const combobox =
                    dropdown.querySelector('[role="combobox"]');

                if (!hiddenInput.value.trim()) {
                    valid = false;
                    if (clearOnly) return;
                    combobox?.classList.add(REVIEW_FORM_ERROR_CLASS);
                    if (!firstInvalidCombobox) firstInvalidCombobox = combobox;
                } else {
                    combobox?.classList.remove(REVIEW_FORM_ERROR_CLASS);
                }
            }
        );
        if (!clearOnly) firstInvalidCombobox?.focus();

        return valid;
    }

    /* =========================
       AJAX SUBMIT
    ========================== */


    async onSubmit(e) {
        e.preventDefault();

        if (!this.validateStep(this.currentStep)) return;

        const submitBtn = this.form.querySelector('button[type="submit"]');
        const loadingText = submitBtn?.dataset.loadingText;

        if (submitBtn && loadingText) {
            submitBtn.dataset.submitOriginalHtml = submitBtn.innerHTML;
            submitBtn.disabled = true;
            submitBtn.innerHTML = loadingText;
        }

        const formData = new FormData(this.form);

        try {
            const res = await fetch(this.form.action, {
                method: this.form.method || 'POST',
                body: formData,
                headers: { Accept: 'application/json' },
            });



            if (!res.ok) throw new Error();

            this.sendNotification(formData);
            this.showSuccess();
        } catch {
            console.error('Submission failed');
            if (submitBtn && submitBtn.dataset.submitOriginalHtml !== undefined) {
                submitBtn.disabled = false;
                submitBtn.innerHTML = submitBtn.dataset.submitOriginalHtml;
            }
        }
    }



    /* =========================
       EMAIL NOTIFICATION (WEB3FORMS)
    ========================== */

    // FormBackend's free plan sends no emails, so a copy of the text fields
    // goes to Web3Forms, which mails it to us. Files are left out (not on
    // their free plan); those stay in FormBackend. Never blocks the success
    // step: a failed notification only logs.
    sendNotification(formData) {
        const accessKey = this.form.dataset.notifyKey;
        if (!accessKey) return;

        const payload = {
            access_key: accessKey,
            subject: 'A new review has been submitted',
            from_name: 'TWOZERO',
            note: 'A new review has been posted on the website. If any photos and/or videos have been attached, they can be found in FormBackend: https://www.formbackend.com/users/sign_in',
        };

        const email = formData.get('email');
        if (email) payload.replyto = email;

        for (const key of new Set(formData.keys())) {
            if (key === 'my_files[]') continue;
            const values = formData.getAll(key).filter((v) => typeof v === 'string' && v);
            if (values.length) payload[key.replace(/\[\]$/, '')] = values.join(', ');
        }

        fetch('https://api.web3forms.com/submit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true,
        }).catch(() => console.error('Review notification failed'));
    }

    showSuccess() {
        const submitBtn = this.form?.querySelector('button[type="submit"]');
        if (submitBtn) {
            submitBtn.disabled = false;
            const orig = submitBtn.dataset.submitOriginalHtml;
            if (orig !== undefined) submitBtn.innerHTML = orig;
        }
        this.currentStep = 'success';
        this.update();
        this.querySelector('[data-step="success"]')?.focus();
    }

    /* =========================
       STAR RATING (HOVER + SELECTED)
    ========================== */

    initStarRating() {
        const container = this.querySelector('[data-rating-stars]');
        if (!container) return;
        const labels = container.querySelectorAll('label');
        const inputs = container.querySelectorAll('input[type="radio"][name="rating"]');

        const updateFill = (filledCount) => {
            labels.forEach((label, i) => {
                label.classList.toggle('star-filled', i < filledCount);
            });
        };

        const getSelectedValue = () => {
            const checked = container.querySelector('input[name="rating"]:checked');
            return checked ? parseInt(checked.value, 10) : 0;
        };

        labels.forEach((label, i) => {
            const value = i + 1;
            label.addEventListener('mouseenter', () => updateFill(value));
        });
        container.addEventListener('mouseleave', () => updateFill(getSelectedValue()));
        inputs.forEach((input) => {
            input.addEventListener('change', () => updateFill(getSelectedValue()));
        });
        updateFill(getSelectedValue());
    }

    /* =========================
       CHARACTER COUNTERS
    ========================== */

    initCharCounters() {
        this.querySelectorAll('[maxlength]').forEach((input) => {
            const counter =
                input.parentElement.querySelector('p');
            if (!counter) return;

            const max = Number(input.getAttribute('maxlength'));

            const update = () => {
                counter.textContent =
                    max - input.value.length + ' ' + reviewStrings.characters_remaining;
            };

            update();
            input.addEventListener('input', update);
        });
    }

    /* =========================
       FILE UPLOAD
    ========================== */

    initFileUpload() {
        const input = this.querySelector('#my_files');
        if (!input) return;

        const previewList = input.closest('div').querySelector('[data-preview-list]');
        const uploadLabel = previewList?.querySelector('label[for="my_files"]');
        const template = this.querySelector('#preview') || document.getElementById('preview');
        const errorEl = input.closest('div').querySelector('[data-media-error]');
        const errorMessageEl = errorEl?.querySelector('[data-media-error-message]');

        if (!template || !uploadLabel) return;

        this._previewFiles = [];

        const showMediaError = (message) => {
            if (errorEl && errorMessageEl) {
                errorMessageEl.textContent = message;
                errorEl.hidden = false;
            }
        };

        const clearMediaError = () => {
            if (errorEl && errorMessageEl) {
                errorMessageEl.textContent = '';
                errorEl.hidden = true;
            }
        };

        const syncInputFiles = () => {
            const dt = new DataTransfer();
            this._previewFiles.forEach((f) => dt.items.add(f));
            input.files = dt.files;
        };

        const addPreview = (file) => {
            if (file.size > 20 * 1024 * 1024) {
                showMediaError(reviewStrings.media_upload_error_message_max_size);
                return false;
            }

            const clone = template.content.cloneNode(true);
            const li = clone.querySelector('[data-preview="item"]');
            const img = clone.querySelector('[data-preview="image"]');
            const removeBtn = clone.querySelector('[data-preview="remove"]');

            const url = URL.createObjectURL(file);

            if (file.type.startsWith('image')) {
                img.src = url;
                img.alt = file.name;
            } else {
                const video = document.createElement('video');
                video.src = url;
                video.className = 'inline-block absolute inset-[0%] size-full max-w-full object-cover';
                video.muted = true;
                video.playsInline = true;
                img.replaceWith(video);
            }

            removeBtn.addEventListener('click', () => {
                const idx = this._previewFiles.indexOf(file);
                if (idx > -1) {
                    this._previewFiles.splice(idx, 1);
                    URL.revokeObjectURL(url);
                    li.remove();
                    syncInputFiles();
                }
            });

            this._previewFiles.push(file);
            previewList.insertBefore(clone, uploadLabel);
            return true;
        };

        input.addEventListener('change', () => {
            const newFiles = Array.from(input.files);
            clearMediaError();

            const totalCount = this._previewFiles.length + newFiles.length;
            if (totalCount > 3) {
                showMediaError(reviewStrings.media_upload_error_message);
                input.value = '';
                return;
            }

            let addedCount = 0;
            for (const file of newFiles) {
                if (!addPreview(file)) {
                    for (let i = 0; i < addedCount; i++) {
                        this._previewFiles.pop();
                        const items = previewList.querySelectorAll('[data-preview="item"]');
                        const last = items[items.length - 1];
                        if (last) {
                            const src = last.querySelector('img[src^="blob:"]')?.src
                                || last.querySelector('video[src^="blob:"]')?.src;
                            if (src) URL.revokeObjectURL(src);
                            last.remove();
                        }
                    }
                    input.value = '';
                    return;
                }
                addedCount++;
            }
            syncInputFiles();
        });
    }
}

customElements.define('review-form', ReviewForm);

/* ======================================================
   CUSTOM DROPDOWN
====================================================== */

class CustomDropdown extends HTMLElement {
    constructor() {
        super();
        this.onOptionClick = this.onOptionClick.bind(this);
        this.onOutsideClick = this.onOutsideClick.bind(this);
        this.onFilterInput = this.onFilterInput.bind(this);
        this.onSearchActivate = this.onSearchActivate.bind(this);
        this.onSearchKeyDown = this.onSearchKeyDown.bind(this);
        this.onSearchBlur = this.onSearchBlur.bind(this);
        this.onFocusIn = this.onFocusIn.bind(this);
    }

    connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.mode = this.dataset.mode;
        this.searchMode = this.dataset.searchable || '';
        const parsedMaxSelections = parseInt(this.dataset.maxSelections, 10);
        this.maxSelections =
            Number.isFinite(parsedMaxSelections) && parsedMaxSelections > 0
                ? parsedMaxSelections
                : Infinity;

        this.combobox = this.querySelector('[role="combobox"]');
        this.listbox = this.querySelector('[role="listbox"]');
        this.panel = this.listbox;
        this.options = Array.from(this.querySelectorAll('[role="option"]'));
        this.hiddenInput = this.querySelector('input[type="hidden"]');
        this.valueLabel =
            this.combobox?.querySelector('[data-dropdown-value]') ||
            this.combobox?.querySelector('div');
        this.selectedValuesEl = this.combobox?.querySelector('[data-selected-values]');
        this.selectedValuesSeparator = this.combobox?.querySelector(
            '[data-selected-values-separator]'
        );

        this.isOverlaySearch =
            this.searchMode === 'country' || this.searchMode === 'overlay';
        this.isInlineSearch = this.searchMode === 'inline';

        if (this.isOverlaySearch || this.isInlineSearch) {
            this.field = this.querySelector('[data-dropdown-field]');
            this.searchOverlay = this.field?.querySelector(
                '[data-dropdown-search-overlay], [data-country-search-overlay]'
            );
            this.searchInput = this.field?.querySelector(
                '[data-dropdown-filter], [data-country-filter]'
            );
            this.filterEmpty = this.field?.querySelector(
                '[data-dropdown-filter-empty], [data-country-filter-empty]'
            );
        }

        if (this.searchInput) {
            this.searchInput.addEventListener('input', this.onFilterInput);
            this.searchInput.addEventListener('blur', this.onSearchBlur);
            if (this.isInlineSearch) {
                this.searchInput.addEventListener('focus', this.onSearchActivate);
                this.searchInput.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.onSearchActivate();
                });
                if (this.mode === 'multiple-values') {
                    this.searchInput.addEventListener('keydown', this.onSearchKeyDown);
                }
            } else {
                this.searchInput.addEventListener('click', (e) => e.stopPropagation());
            }
        }

        if (!this.combobox || !this.hiddenInput || !this.valueLabel) return;

        this._panelBelowClasses = [];
        if (this.panel.classList.contains('top-full')) {
            this._panelBelowClasses.push('top-full');
        }
        if (this.panel.classList.contains('top-[100%]')) {
            this._panelBelowClasses.push('top-[100%]');
        }
        if (this.panel.classList.contains('bottom-[auto]')) {
            this._panelBelowClasses.push('bottom-[auto]');
        }
        if (this.panel.classList.contains('mt-1')) {
            this._panelBelowClasses.push('mt-1');
        }

        this.placeholder = this.valueLabel.textContent.trim();
        this.placeholderHtml = this.valueLabel.innerHTML;

        if (this.isInlineSearch && this.searchInput) {
            this.searchPlaceholder = this.searchInput.getAttribute('placeholder') || '';
        }

        if (this.mode === 'multiple-values') {
            this.selectionOrder = [];
        }

        this.combobox.addEventListener('click', () => this.toggle());
        this.options.forEach((opt) => {
            opt.addEventListener('mousedown', (e) => e.preventDefault());
            opt.addEventListener('click', this.onOptionClick);
        });

        document.addEventListener('click', this.onOutsideClick);
        document.addEventListener('focusin', this.onFocusIn);
    }

    disconnectedCallback() {
        document.removeEventListener('click', this.onOutsideClick);
        document.removeEventListener('focusin', this.onFocusIn);
        this.searchInput?.removeEventListener('input', this.onFilterInput);
        this.searchInput?.removeEventListener('blur', this.onSearchBlur);
        this.searchInput?.removeEventListener('focus', this.onSearchActivate);
        this.searchInput?.removeEventListener('keydown', this.onSearchKeyDown);
    }

    onFocusIn(e) {
        if (!this.isOpen() || this.contains(e.target)) return;
        this.close();
    }

    onSearchBlur() {
        if (!this.searchInput || !this.isOpen()) return;
        this.close();
    }

    onSearchKeyDown(e) {
        if (e.key !== 'Backspace') return;
        if (this.mode !== 'multiple-values' || !this.isInlineSearch || !this.isOpen()) return;
        if (!this.searchInput || document.activeElement !== this.searchInput) return;
        if (this.searchInput.value !== '') return;
        if (!this.selectionOrder.length) return;

        e.preventDefault();
        this.removeLastSelection();
    }

    removeLastSelection() {
        const lastValue = this.selectionOrder[this.selectionOrder.length - 1];
        const option = this.options.find(
            (o) => o.getAttribute('data-value') === lastValue
        );
        if (!option) {
            this.selectionOrder.pop();
            this.updateMultipleSelectionUI({ clearSearch: false });
            return;
        }

        option.setAttribute('aria-selected', 'false');
        this.selectionOrder = this.selectionOrder.filter(
            (value) => value !== lastValue
        );
        this.updateMultipleSelectionUI({ clearSearch: false });
    }

    updateMultipleSelectionUI({ clearSearch = false } = {}) {
        const atMax =
            this.maxSelections !== Infinity &&
            this.selectionOrder.length >= this.maxSelections;

        this.options.forEach((o) => {
            const isSelected = o.getAttribute('aria-selected') === 'true';
            o.classList.toggle('bg-gray-300', isSelected);
            o.classList.toggle('bg-gray-200', !isSelected);

            const disabled = atMax && !isSelected;
            o.setAttribute('aria-disabled', String(disabled));
            o.classList.toggle('pointer-events-none', disabled);
            o.classList.toggle('opacity-50', disabled);
        });

        const selectedOptions = this.selectionOrder
            .map((value) =>
                this.options.find((o) => o.getAttribute('data-value') === value)
            )
            .filter(Boolean);
        const displayTexts = selectedOptions.map(
            (o) => o.querySelector('span')?.textContent?.trim() || o.textContent.trim()
        );

        this.hiddenInput.value = this.selectionOrder.join(',');

        const hasSelections = selectedOptions.length > 0;

        if (hasSelections) {
            this.renderSelectedValues(selectedOptions);
            this.valueLabel.textContent = displayTexts.join(', ');
            this.combobox.classList.remove('!text-gray-500');
            this.combobox.classList.add('text-gray-600');
        } else {
            this.renderSelectedValues([]);
            this.valueLabel.textContent = this.placeholder;
            this.hiddenInput.value = '';
            this.combobox.classList.remove('text-gray-600');
            this.combobox.classList.add('!text-gray-500');
        }

        if (this.isInlineSearch) {
            this.syncInlineSearchState(hasSelections, { clearSearch });
            requestAnimationFrame(() => this.focusSearchInput());
        }
    }

    onSearchActivate() {
        if (!this.isInlineSearch || this.isOpen()) return;
        this.open({ preserveSearch: true });
    }

    toggle() {
        this.isOpen() ? this.close() : this.open();
    }

    syncOverlaySearchState(isOpen) {
        if (!this.isOverlaySearch) return;

        if (isOpen) {
            this.searchOverlay?.removeAttribute('hidden');
            this.valueLabel?.setAttribute('hidden', '');
        } else {
            this.searchOverlay?.setAttribute('hidden', '');
            this.valueLabel?.removeAttribute('hidden');
        }
    }

    focusSearchInput() {
        if (!this.searchInput) return;

        if (this.isOverlaySearch) {
            this.searchInput.focus();
            requestAnimationFrame(() => {
                if (window.innerWidth < 768) {
                    this.field?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                }
            });
            return;
        }

        this.searchInput.focus({ preventScroll: true });
        if (document.activeElement !== this.searchInput) {
            requestAnimationFrame(() => this.searchInput.focus({ preventScroll: true }));
        }
    }

    syncInlineSearchState(hasSelections, { clearSearch = false } = {}) {
        if (!this.isInlineSearch || !this.searchInput) return;

        if (clearSearch) {
            this.searchInput.value = '';
            this.filterOptions('');
        }

        this.searchInput.placeholder = hasSelections
            ? ''
            : this.searchPlaceholder;
    }

    setPanelPositionBelow() {
        this.panel.classList.remove('bottom-full', 'mb-1', 'top-auto');
        this.panel.classList.add(...this._panelBelowClasses);
    }

    setPanelPositionAbove() {
        this.panel.classList.remove(...this._panelBelowClasses);
        this.panel.classList.add('bottom-full', 'mb-1', 'top-auto');
    }

    positionPanel() {
        this.setPanelPositionBelow();

        const rect = this.panel.getBoundingClientRect();
        if (rect.bottom > window.innerHeight) {
            this.setPanelPositionAbove();
        }
    }

    open({ preserveSearch = false } = {}) {
        this.combobox.setAttribute('aria-expanded', 'true');
        this.panel.classList.remove('hidden');
        this.positionPanel();

        if (this.isOverlaySearch) {
            this.syncOverlaySearchState(true);
        }

        if (this.searchInput) {
            if (!preserveSearch) {
                this.searchInput.value = '';
                this.filterOptions('');
            }
            if (this.isInlineSearch) {
                this.syncInlineSearchState(this.selectionOrder?.length > 0);
            }
            if (document.activeElement !== this.searchInput) {
                this.focusSearchInput();
            }
        }
    }

    close() {
        this.combobox.setAttribute('aria-expanded', 'false');
        this.panel.classList.add('hidden');
        this.setPanelPositionBelow();

        if (this.isOverlaySearch) {
            this.syncOverlaySearchState(false);
        }

        if (this.searchInput) {
            this.searchInput.value = '';
            this.filterOptions('');
            if (this.isInlineSearch) {
                this.syncInlineSearchState(this.selectionOrder?.length > 0);
            } else {
                this.searchInput.blur();
            }
        }
    }

    isOpen() {
        return this.combobox.getAttribute('aria-expanded') === 'true';
    }

    onFilterInput() {
        this.filterOptions(this.searchInput?.value || '');
    }

    filterOptions(query) {
        const q = (query || '').trim().toLowerCase();

        this.options.forEach((option) => {
            const name =
                option.querySelector('span')?.textContent?.trim().toLowerCase() || '';
            const value = (option.getAttribute('data-value') || '').toLowerCase();
            const match = !q || name.includes(q) || value.includes(q);
            option.classList.toggle('hidden', !match);
        });

        if (this.filterEmpty) {
            const anyVisible = this.options.some(
                (option) => !option.classList.contains('hidden')
            );
            this.filterEmpty.toggleAttribute('hidden', anyVisible || !q);
        }
    }

    setOptionSelectedState(option, selected) {
        option.setAttribute('aria-selected', String(selected));
        const display = option.querySelector('[data-option-display]');
        if (!display) return;

        display.classList.toggle('bg-gray-300', selected);
        display.classList.toggle('hover:bg-gray-300', selected);
        display.classList.toggle('bg-gray-100', !selected);
        display.classList.toggle('hover:bg-gray-200', !selected);
    }

    setValueLabelFromOption(option) {
        const display = option.querySelector('[data-option-display]');
        if (display) {
            this.valueLabel.innerHTML = display.innerHTML;
            return;
        }

        this.valueLabel.textContent = option.textContent.trim();
    }

    renderSelectedValues(selectedOptions) {
        if (!this.selectedValuesEl) return;

        this.selectedValuesEl.innerHTML = '';

        if (!selectedOptions.length) {
            this.selectedValuesEl.classList.add('hidden');
            this.selectedValuesSeparator?.classList.add('hidden');
            return;
        }

        this.selectedValuesEl.classList.remove('hidden');
        this.selectedValuesSeparator?.classList.remove('hidden');

        selectedOptions.forEach((option, index) => {
            const text =
                option.querySelector('span')?.textContent?.trim() ||
                option.textContent.trim();
            const span = document.createElement('span');
            const isLast = index === selectedOptions.length - 1;
            span.textContent = isLast ? text : `${text}, `;
            this.selectedValuesEl.appendChild(span);
        });
    }

    onOptionClick(e) {
        const option = e.currentTarget;
        const dataValue = option.getAttribute('data-value') ?? '';

        if (this.mode === 'single-value') {
            this.options.forEach((o) => this.setOptionSelectedState(o, false));
            this.setOptionSelectedState(option, true);

            this.hiddenInput.value = dataValue;
            this.setValueLabelFromOption(option);
            this.combobox.classList.remove('!text-gray-500');
            this.combobox.classList.add('text-gray-600');

            this.close();
        }

        if (this.mode === 'multiple-values') {
            const wasSelected = option.getAttribute('aria-selected') === 'true';

            if (
                !wasSelected &&
                this.selectionOrder.length >= this.maxSelections
            ) {
                return;
            }

            option.setAttribute('aria-selected', String(!wasSelected));

            if (!wasSelected) {
                if (!this.selectionOrder.includes(dataValue)) {
                    this.selectionOrder.push(dataValue);
                }
            } else {
                this.selectionOrder = this.selectionOrder.filter(
                    (value) => value !== dataValue
                );
            }

            this.updateMultipleSelectionUI({ clearSearch: !wasSelected });
        }
    }

    onOutsideClick(e) {
        if (!this.contains(e.target)) this.close();
    }
}

customElements.define('custom-dropdown', CustomDropdown);


if (window.location.search.includes('review=write')) {
    document.querySelector('[data-modal="review-form"]').open();
}